import React, { useState, useRef } from 'react';
import { Link, useHistory } from 'react-router-dom';
import axios from 'axios';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { useFormik } from 'formik';
import { Toast } from 'primereact/toast';
// eslint-disable-next-line import/no-named-as-default
import ReCAPTCHA from 'react-google-recaptcha';
import * as Yup from 'yup';
import dayjs from 'dayjs';

import { encrypt, encryptEmail } from '../../../../utils/security.helper';
import {
  REACT_APP_LOGIN_AUTH,
  REACT_APP_AUTH_CLIENT_ID,
  REACT_APP_RECAPTCHA_KEY,
  REACT_APP_SPECIAL_PERMISSIONS_PF_USERS,
  // REACT_APP_AUTH_REDIRECT_URI,
} from '../../../../constants/envConstants';
import MicrosoftIcon from '../../../../assets/images/microsoft-icon.png';

import './components/Styles/mpinStyle.css';

import Welcome from './Welcome';
import { getCookieValue } from './components/helper/helper';
import SsoCallback from './components/SsoCallback';

const SHOW_RECAPTCHA_MAX_LIMIT = 1;

const Login = () => {
  const toast = useRef(null);
  const history = useHistory();
  const authorizeForm = useRef(React.createRef());
  const [formData, setFormData] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  // ── SSO state (additive) ────────────────────────────────────────────────
  const [ssoState, setSsoState] = useState({
    mode: false,
    loading: false,
  });
  // ────────────────────────────────────────────────────────────────────────
  const loginSubmitCount = parseInt(
    localStorage.getItem('loginSubmitCount') || 0
  );
  const tempKey = localStorage.getItem('G7sKf92LaZ8eN1vPq4Xt') || undefined;
  const loginValidation = {
    email: Yup.string()
      .required('Email field is required.')
      .email('Email must be a valid email.')
      .matches(/^(?!.*@[^,]*,)/),
    password: Yup.string().required('Password field is required.'),
    reCaptcha:
      loginSubmitCount >= SHOW_RECAPTCHA_MAX_LIMIT
        ? Yup.string().required('Please complete the reCAPTCHA.').nullable()
        : Yup.string().nullable(),
  };

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
      reCaptcha: '',
    },
    validationSchema: Yup.object().shape(loginValidation),
    onSubmit: values => {
      setLoading(true);
      const username = values.email;
      const password = values.password;
      const reCaptcha = values.reCaptcha;
      const isPFUser = values.email.includes('@projectsforce.com') ?? false;
      localStorage.setItem('isPFUser', isPFUser);
      if (
        Array.isArray(REACT_APP_SPECIAL_PERMISSIONS_PF_USERS) &&
        REACT_APP_SPECIAL_PERMISSIONS_PF_USERS.length === 0
      ) {
        localStorage.setItem('isSpecialPFUser', true);
        localStorage.setItem('betaTestEnabled', true);
      } else if (
        REACT_APP_SPECIAL_PERMISSIONS_PF_USERS.includes(values.email)
      ) {
        localStorage.setItem('isSpecialPFUser', true);
        localStorage.setItem('betaTestEnabled', true);
      }
      axios
        .post(
          `${REACT_APP_LOGIN_AUTH}/check.v1`,
          {
            username,
            password: encrypt(password),
            reCaptcha,
            G7sKf92LaZ8eN1vPq4Xt: tempKey,
            method: 'POST',
          },
          {
            headers: {
              'device-type': 'web_app',
            },
          }
        )
        .then(response => {
          if (response?.data?.status) {
            // setVisible(true);
            if (response?.data?.otpVerified) {
              localStorage?.setItem('syncLoginEvent', true);
              localStorage.setItem('loginTime', dayjs().utc().format());
              localStorage.removeItem('Auth_Verify');
              setFormData(values);
              authorizeForm.current.submit();
            } else {
              history.push(`/verify-otp?token=${encryptEmail(values.email)}`);
              return null;
            }
            toast.current.show({
              severity: 'success',
              summary: 'Success',
              detail: 'Login Successfully',
            });
          }
          if (response?.data?.message === "Username or password didn't match") {
            localStorage.setItem('loginSubmitCount', loginSubmitCount + 1);
            toast.current.show({
              severity: 'error',
              summary: 'Login Failed',
              detail: 'Invalid Credentials',
            });
            setLoading(false);
          }
        })
        .catch(error => {
          localStorage.setItem('loginSubmitCount', loginSubmitCount + 1);
          toast.current.show({
            severity: 'error',
            summary: 'Login Failed',
            detail: error?.response?.data?.message || 'Invalid Credentials',
          });
          setLoading(false);
        });
    },
  });

  const handleToggle = () => {
    setShowPassword(current => !current);
  };

  // ── SSO handlers (additive) ─────────────────────────────────────────────
  const handleEnterSsoMode = () => {
    setSsoState(prev => ({ ...prev, mode: true }));
  };

  const handleCancelSsoMode = () => {
    setSsoState({
      mode: false,
      loading: false,
    });
  };

  const handleSsoSubmit = async () => {
    // Reuse the existing email field; trigger validation before proceeding.
    /* const emailError =  */ await formik.validateField('email');
    if (!formik.values.email || formik.errors.email) {
      formik.setFieldTouched('email', true);
      return;
    }
    try {
      setSsoState(prev => ({ ...prev, loading: true }));
      const response = await axios.post(
        `${REACT_APP_LOGIN_AUTH}/sso-auth/saml/microsoft/initiate`,
        {
          email: formik.values.email,
          app_client_id: REACT_APP_AUTH_CLIENT_ID,
          response_type: 'code',
          state: '/',
          // deviceType: 'web_app',
          // redirect_uri: REACT_APP_AUTH_REDIRECT_URI || window.location.origin,
        }
      );
      if (response?.data?.redirectUrl) {
        localStorage.setItem('isSsoLogin', true);
        const isPFUser =
          formik.values.email.includes('@projectsforce.com') ?? false;
        localStorage.setItem('isPFUser', isPFUser);
        localStorage?.setItem('syncLoginEvent', true);
        localStorage.setItem('loginTime', dayjs().utc().format());
        localStorage.removeItem('Auth_Verify');
        if (
          Array.isArray(REACT_APP_SPECIAL_PERMISSIONS_PF_USERS) &&
          REACT_APP_SPECIAL_PERMISSIONS_PF_USERS.length === 0
        ) {
          localStorage.setItem('isSpecialPFUser', true);
          localStorage.setItem('betaTestEnabled', true);
        } else if (
          REACT_APP_SPECIAL_PERMISSIONS_PF_USERS.includes(formik.values.email)
        ) {
          localStorage.setItem('isSpecialPFUser', true);
          localStorage.setItem('betaTestEnabled', true);
        }
        // Full browser navigation to Microsoft Entra login page.
        // window.location.href = response.data.redirectUrl;
        window.open(response?.data?.redirectUrl, '_self');
      } else {
        throw new Error('Unexpected response from SSO initiation.');
      }
    } catch (ssoError) {
      setSsoState(prev => ({ ...prev, loading: false }));
      toast.current.show({
        severity: 'error',
        summary: 'SSO Login Failed',
        detail:
          ssoError?.response?.data?.message ||
          'Microsoft Entra login is not available. Please try again.',
      });
    }
  };
  // ───────────────────────────────────────────────────────────────────────

  const isMpinSet = localStorage.getItem('is_mpin_enabled');
  const refreshToken = getCookieValue('refreshToken');

  if (isMpinSet === 'true' && refreshToken) {
    window.location.href = `${window.location.origin}/verify-pin`;
    return null;
  }

  return (
    <>
      <Toast ref={toast} />
      <form
        ref={authorizeForm}
        action={`${REACT_APP_LOGIN_AUTH}/authorize.v1`}
        method="post"
      >
        <input type="hidden" name="username" value={formData.email} />
        <input
          type="hidden"
          name="password"
          value={encrypt(formData.password)}
        />
        <input
          type="hidden"
          name="client_id"
          value={REACT_APP_AUTH_CLIENT_ID}
        />
        {tempKey && (
          <input type="hidden" name="G7sKf92LaZ8eN1vPq4Xt" value={tempKey} />
        )}
        <input type="hidden" name="response_type" value="code" />
        <input type="hidden" name="state" value="/" />
      </form>
      <div
        className={`LoginContainer flex align-items-center justify-content-center overflow-auto`}
      >
        <div className="max-h-full">
          <img
            src="/images/circle-background.png"
            alt="Project Force"
            className="circle-bg mb-3"
            // style={{ maxWidth: '350px' }}
          />
          <div className="text-center pt-5">
            <img
              src="/images/PF-white-logo.png"
              alt="Project Force"
              className="mb-3"
              style={{ maxWidth: '180px' }}
            />
            <p className="px-3" style={{ maxWidth: '470px', margin: 'auto' }}>
              ProjectsForce puts you in control of your work, keeping both you
              and your clients on-track towards completing the job and getting
              paid.{' '}
            </p>
          </div>
          <div className="flex col-12 pb-0">
            <Welcome />
            <div className="flex col-6 flex h-100 w-100 align-center wrap px-5 pt-0 pb-1 login-container-right ">
              <div className="flex login-right-inner w-12">
                <div className="login-section w-12 py-4">
                  <SsoCallback />
                  <h1 className="text-center mt-4 md:mt-0">LOG IN</h1>
                  <form onSubmit={formik.handleSubmit}>
                    <div>
                      <div
                        className={`${
                          formik.touched.email && formik.errors.email
                            ? 'validation-error'
                            : ''
                        } col-12 p-0 p-input-icon-right mb-3 `}
                      >
                        <span className="p-float-label">
                          <i className="pi pi-user"></i>
                          <InputText
                            autoComplete="off"
                            id="email"
                            onChange={formik.handleChange}
                            value={formik.values.email}
                            onBlur={formik.handleBlur}
                          />
                          <label htmlFor="email" className="margin-left-175">
                            Email*
                          </label>
                        </span>

                        {formik.touched.email && formik.errors.email ? (
                          <div className="error">{formik.errors.email}</div>
                        ) : null}
                      </div>

                      {/* Password field — hidden in SSO mode */}
                      {!ssoState.mode && (
                        <>
                          <div
                            className={`${
                              formik.touched.password && formik.errors.password
                                ? 'validation-error'
                                : ''
                            } col-12 p-0 p-input-icon-right mb-3`}
                          >
                            <span className="p-float-label">
                              <i className="pi pi-lock"></i>
                              <InputText
                                type={showPassword ? 'text' : 'password'}
                                autoComplete="off"
                                id="password"
                                onChange={formik.handleChange}
                                value={formik.values.password}
                                onBlur={formik.handleBlur}
                              />
                              <label
                                htmlFor="password"
                                className="margin-left-175"
                              >
                                Password*
                              </label>
                              {showPassword ? (
                                <i
                                  className="pi pi-eye-slash"
                                  role="button"
                                  tabIndex={0}
                                  onClick={handleToggle}
                                  onKeyDown={e => {
                                    if (e?.key === 'Enter' || e?.key === ' ') {
                                      handleToggle();
                                    }
                                  }}
                                ></i>
                              ) : (
                                <i
                                  className="pi pi-eye"
                                  role="button"
                                  tabIndex={0}
                                  onClick={handleToggle}
                                  onKeyDown={e => {
                                    if (e?.key === 'Enter' || e?.key === ' ') {
                                      handleToggle();
                                    }
                                  }}
                                ></i>
                              )}
                            </span>
                            {formik.touched.password &&
                            formik.errors.password ? (
                              <div className="error">
                                {formik.errors.password}
                              </div>
                            ) : null}
                          </div>

                          {loginSubmitCount >= SHOW_RECAPTCHA_MAX_LIMIT && (
                            <div
                              className={` col-12 p-0 p-input-icon-right mb-3`}
                            >
                              <ReCAPTCHA
                                sitekey={REACT_APP_RECAPTCHA_KEY}
                                onChange={captcha =>
                                  formik.setFieldValue('reCaptcha', captcha)
                                }
                              />
                              {formik.errors.reCaptcha ? (
                                <div className="error">
                                  {formik.errors.reCaptcha}
                                </div>
                              ) : null}
                            </div>
                          )}
                        </>
                      )}

                      {!ssoState.mode && (
                        <div className="mt-2">
                          <Link to="/forget-password">Forgot Password ?</Link>
                        </div>
                      )}
                      <div className="mt-2">
                        By using ProjectsForce, you agree to our{' '}
                        <a href="https://projectsforce.com/privacy-policy">
                          Privacy Policy
                        </a>
                        .
                      </div>
                      {!ssoState.mode ? (
                        <>
                          <Button
                            icon={loading ? 'pi pi-spin pi-spinner' : ''}
                            severity="primary"
                            type="submit"
                            label="Log in"
                            pt={{
                              label: {
                                className: 'flex-none w-4rem',
                              },
                            }}
                            style={{
                              background: '#0074d9',
                              border: '1px solid #0074d9',
                            }}
                            className="mt-3 flex justify-content-center w-full mt-3 flex-row-reverse"
                            disabled={loading}
                          ></Button>

                          {/* ── Microsoft Entra SSO (additive) ───────────────── */}
                          <Button
                            type="button"
                            label="Login with Microsoft Entra SSO"
                            severity="primary"
                            icon={
                              ssoState.loading ? 'pi pi-spin pi-spinner' : ''
                            }
                            pt={{
                              label: { className: 'flex-none' },
                            }}
                            style={{
                              background: '#2f2f2f',
                              border: '1px solid #2f2f2f',
                            }}
                            className="mt-2 flex justify-content-center w-full flex-row-reverse align-items-center gap-2 bg-gray-900 border-gray-900"
                            onClick={handleEnterSsoMode}
                          >
                            <img
                              src={MicrosoftIcon}
                              alt="Microsoft"
                              className="w-1rem h-1rem"
                            />
                          </Button>
                        </>
                      ) : (
                        <div className="flex flex-column gap-2 mt-2 align-items-center">
                          <Button
                            type="button"
                            label="Continue with Microsoft Entra SSO"
                            severity="primary"
                            pt={{
                              label: { className: 'flex-none' },
                            }}
                            style={{
                              background: '#0074d9',
                              border: '1px solid #0074d9',
                            }}
                            className="mt-2 flex justify-content-center w-full flex-row-reverse align-items-center gap-2"
                            disabled={
                              ssoState.loading ||
                              !formik.values.email ||
                              (formik.touched.email && formik.errors.email)
                            }
                            onClick={handleSsoSubmit}
                            loading={ssoState.loading}
                            icon={
                              ssoState.loading ? 'pi pi-spin pi-spinner' : ''
                            }
                          >
                            <img
                              src={MicrosoftIcon}
                              alt="Microsoft"
                              className="w-1rem h-1rem"
                            />
                          </Button>
                          <Button
                            type="button"
                            label="Back"
                            style={{
                              border: '1px solid #0074d9',
                            }}
                            outlined
                            className="w-full text-center"
                            disabled={ssoState.loading}
                            onClick={handleCancelSsoMode}
                          />
                        </div>
                      )}
                      {/* ── end Microsoft Entra SSO ──────────────────────── */}
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
